import { Country, State } from "country-state-city";

export type LocationValue = {
	country: string | null;
	state: string | null;
	city: string | null;
};

/**
 * Resolves ISO codes back to human names. Returns a "City, Region, Country"
 * string suitable for the public profile, or null if the country can't be
 * resolved (which shouldn't happen for valid stored data).
 *
 * Lives in its own module so server components can import it; the
 * cascading picker that uses it is `"use client"` and would otherwise
 * poison this helper for RSC use.
 */
export function formatLocation(value: LocationValue): string | null {
	if (!value.country) {
		return null;
	}
	const countryName = Country.getCountryByCode(value.country)?.name;
	if (!countryName) {
		return null;
	}
	const stateName =
		value.country && value.state
			? State.getStateByCodeAndCountry(value.state, value.country)?.name
			: null;
	const parts = [value.city, stateName, countryName].filter((p): p is string =>
		Boolean(p?.trim()),
	);
	return parts.join(", ");
}

/**
 * Human label for a telehealth jurisdiction (state/province name, falling back
 * to the country name when the region has no subdivision).
 */
export function formatRegion(
	country: string,
	state: string | null,
): string | null {
	if (state) {
		const stateName = State.getStateByCodeAndCountry(state, country)?.name;
		if (stateName) {
			return stateName;
		}
	}
	return Country.getCountryByCode(country)?.name ?? null;
}

/**
 * Ordered, de-duplicated human labels for a therapist's telehealth coverage.
 * When `primary` is provided (the home jurisdiction is served remotely), its
 * region leads the list, followed by the explicit telehealth jurisdictions.
 * The primary can't also exist as a telehealth row (DB unique constraint on
 * profileId+country+state), but we de-dupe defensively.
 */
export function telehealthRegionLabels(
	regions: { country: string; state: string | null }[],
	primary?: { country: string | null; state: string | null } | null,
): string[] {
	const labels: string[] = [];
	const seen = new Set<string>();
	const add = (label: string | null) => {
		if (label && !seen.has(label)) {
			seen.add(label);
			labels.push(label);
		}
	};
	if (primary?.country) {
		add(formatRegion(primary.country, primary.state ?? null));
	}
	for (const region of regions) {
		add(formatRegion(region.country, region.state));
	}
	return labels;
}
